"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CircleCheck as CheckCircle2, FileText, CircleQuestionMark as HelpCircle, PhoneCall, ShieldAlert, Sparkles } from 'lucide-react';
export default function BasementRemodelingFaq() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  const faqs = [{
    id: "faq-waterproofing",
    question: "How do you manage moisture, humidity, and drainage before finishing a basement in Brookhaven?",
    icon: ShieldAlert,
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Brookhaven homes often sit on clay-heavy Georgia red clay that expands with seasonal rain, making comprehensive below-grade moisture management step one. We perform a calibrated relative humidity check, inspect the foundation slab for hydrostatic pressure, and seal existing perimeter wall fractures before hanging framing or insulation.
          </p>
          <ul className="space-y-1.5 pl-1 list-none">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Closed-cell spray foam or rigid XPS moisture barriers behind exterior basement walls.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Dedicated dehumidification circuits integrated into your new HVAC sub-zone.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Exterior french drain or interior perimeter trench evaluation when grading warrants it.</span>
            </li>
          </ul>
        </div>
  }, {
    id: "faq-permits",
    question: "What permits are required for Brookhaven and DeKalb County basement conversions?",
    icon: FileText,
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Any habitable basement renovation within Brookhaven city limits requires a combined residential building permit issued through the City of Brookhaven Community Development Department, coordinated with DeKalb County water and health ordinances.
          </p>
          <p>
            Our project management team drafts and submits all architectural floor plans, calculates load-bearing beam modifications, and pulls all master electrical, plumbing, and mechanical permits. We also handle every inspection milestone—rough framing, rough-in trades, insulation, and the final Certificate of Completion.
          </p>
        </div>
  }, {
    id: "faq-egress",
    question: "Are egress windows or exterior walk-outs mandatory for bedrooms and living spaces?",
    icon: Sparkles,
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Under Georgia State Minimum Standard One- and Two-Family Dwelling Code (IRC), every basement bedroom or sleeping room <em>must</em> have at least one operable emergency escape and rescue opening. If you are adding a guest suite, an egress window or direct exterior walk-out is legally required.
          </p>
          <div className="p-3 rounded-md bg-muted/60 border border-border text-xs leading-normal">
            <strong>Code minimums we engineer:</strong> Minimum 5.7 sq. ft. net clear opening (5.0 sq. ft. at grade), minimum 24-inch clear height, minimum 20-inch clear width, with sill heights no greater than 44 inches off the finished floor and code-approved window wells with integrated escape ladders.
          </div>
        </div>
  }, {
    id: "faq-living",
    question: "Can our family stay in the home during a full basement build-out?",
    icon: CheckCircle2,
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Yes. Over 90% of our Brookhaven clients remain comfortably in their main-level living quarters throughout the entire project. Because the work is isolated subterranean space, we create clean, isolated work barriers.
          </p>
          <ul className="space-y-1.5 pl-1 list-none">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Heavy-duty zipper containment walls and negative air machines with HEPA scrubbers at stairwells.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Tradespeople access the site exclusively through exterior basement walk-outs or Bilco doors whenever available.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Scheduled utility shutdowns for electrical panel upgrades or plumbing tie-ins announced 48 hours prior.</span>
            </li>
          </ul>
        </div>
  }, {
    id: "faq-timeline",
    question: "What is the typical timeline from architectural drafting to final walkthrough?",
    icon: HelpCircle,
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            A comprehensive basement renovation typically spans <strong>8 to 14 weeks of active construction</strong> following the initial design and permitting window:
          </p>
          <ol className="space-y-1 pl-4 list-decimal text-xs md:text-sm">
            <li><strong>Design &amp; City Permitting (3-5 weeks):</strong> Spatial planning, CAD layouts, material selections, and municipal approval.</li>
            <li><strong>Rough Trades &amp; Framing (3-4 weeks):</strong> Wall framing, moisture encapsulation, rough plumbing for bathrooms/wet bars, electrical, and HVAC runs.</li>
            <li><strong>Insulation &amp; Drywall (2-3 weeks):</strong> Sound-dampening insulation, board hanging, taping, smooth-coat finishing, and prime painting.</li>
            <li><strong>Finish Carpentry &amp; Fixtures (3-4 weeks):</strong> Custom wet bars, cabinetry, luxury vinyl plank or engineered hardwood, light fixtures, glass shower enclosures, and final building inspection.</li>
          </ol>
        </div>
  }];
  return <section id="basementremodelingfaq" data-nav="light" ref={sectionRef} className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-t border-border">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Left Column: Visual feature and context */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-accent text-accent-foreground border border-border">
                <Sparkles className="size-3.5 text-primary" />
                <span>Brookhaven &amp; DeKalb County Guidelines</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-tight">
                Frequently asked questions about basement remodeling.
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                Transforming unfinished lower-level square footage adds high-yield living area to your Brookhaven home. Here is what homeowners regularly ask regarding permits, foundation prep, moisture control, and construction flow.
              </p>
            </div>

            {/* Visual Photography Card */}
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md group">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement_remodel.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement_remodel.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement_remodel.png-wvc-sizes" alt="Custom finished basement in Brookhaven with built-in wet bar, recessed lighting, and modern entertainment area" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5 flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span className="text-foreground font-semibold">Historic Brookhaven Residence</span>
                  <span>Lower-level suite conversion</span>
                </div>
                <p className="text-xs text-muted-foreground leading-normal">
                  Custom entertainment wall, moisture-sealed subflooring, acoustic ceiling isolation, and code-approved guest egress.
                </p>
              </div>
            </div>

            {/* Quick Contact CTA strip */}
            <div className="p-6 rounded-xl bg-muted/40 border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground">Have a unique basement footprint?</h3>
                <p className="text-xs text-muted-foreground">
                  We offer on-site moisture and structural evaluations across Brookhaven.
                </p>
              </div>
              <Button asChild variant="default" size="sm" className="shrink-0">
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-1.5">
                  <PhoneCall className="size-3.5" />
                  <span>Request Estimate</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Accordion Items */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="faq-waterproofing" className="w-full space-y-4">
              {faqs.map((faq, index) => {
              const IconComp = faq.icon;
              return <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border rounded-xl px-5 bg-card shadow-xs transition-colors data-[state=open]:border-primary/40 data-[state=open]:shadow-sm">
                    <AccordionTrigger className="py-5 hover:no-underline text-left cursor-pointer group">
                      <div className="flex items-start gap-3.5 pr-2">
                        <div className="size-8 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0 mt-0.5 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <IconComp className="size-4" />
                        </div>
                        <span className="text-base font-medium font-serif text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-6 px-1 text-sm border-t border-border/40 mt-1">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}